// src/encounter/services/use-eprescription.ts
// ePrescription (eRx → DHPO) for the Wrap-Up header cluster.
//
// I previously reported this surface as BLOCKED "no eRx endpoint in React". That was wrong —
// React had no CLIENT, but the endpoints have always existed. Corrected 2026-08-06 by reading
// `encounter_data_source_impl.dart`:
//
//   preview  GET  /api/EPriscription/GetPrescriptionData?encounterId={id}      (:1049, :1067)
//   submit   POST /api/EPriscription/SendEPrescriptionToDHPO?encounterId={id}  (:1090)
//   gate     POST /api/Doctor/CheckEPrescriptionLoginEnabled {providerId, practiceId} → bool
//                                                                                (:1027-1030)
//
// ⚠ THE BASE PATH IS MISSPELLED SERVER-SIDE: `/api/EPriscription` — "EPriscription", not
// "EPrescription" (api_constants.dart:377-378, `PatientService.EPrescription`). Reproduced
// verbatim. Do NOT "fix" the spelling; the route would 404.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { showServerError, showSuccess } from '@/shared/lib/toast-helpers';

/** Misspelled server-side — see the header note. */
const ERX_BASE = '/api/EPriscription';

/**
 * The preview payload. Field names verbatim from Flutter `eprescription_preview_model.dart`
 * (Prescription :399-409, PatientDetails :319-327, Allergy :200-201, Diagnosis :230-233).
 *
 * WIDENED 2026-09-07 against the LIVE dev response, captured by intercepting the app's own
 * `GetPrescriptionData` call. Two corrections came out of that capture:
 *
 *   1. ⚠️ THE PATIENT KEY WAS WRONG. This interface declared `patient`; the wire sends
 *      **`patientDetails`**. `preview.patient` was therefore always `undefined`, and the dialog's
 *      `{patient && …}` guard meant the ENTIRE identity block silently rendered nothing — the
 *      single biggest reason the React preview looked nothing like the deployed document. There
 *      was no type error to catch it, because the field was optional.
 *   2. `practice`, `organization` and `sponsor` ARE on the wire and were simply untyped. The
 *      old note below claimed the preview "renders the prescription document only"; the live
 *      Flutter app renders the full letterhead, so they are typed now.
 *
 * Measured response shape (dev, encounter a1e1cca8-…): allergies[] · prescriptions[] ·
 * ePerscription{ePrescriptionReference,status} · patientDetails{…} · diagnoses[] ·
 * practice{name,address,phone,email} · organization{id,name,logoUrl} ·
 * sponsor{sponsorName,sponsorType,policyNo,plan,tpa}.
 *
 * Still NOT on the wire: `practiceStampUrl` / `doctorSignatureUrl`. Flutter's `SignatureWidget`
 * draws an empty 60px slot above the rule when the URL is blank (signature_widget.dart:18), which
 * is exactly what the deployed document shows — so the signature block ports as the ruled labels
 * with no image, not as a fabricated one.
 */
export interface EPrescriptionPreviewPatient {
  name?: string | null;
  dob?: string | null;
  gender?: string | null;
  heightWeight?: string | null;
  mrn?: string | null;
  network?: string | null;
  provider?: string | null;
  encounterDate?: string | null;
}

/** A prescribed medication line (Flutter `Prescription`). */
export interface EPrescriptionLine {
  id?: string | null;
  name?: string | null;
  code?: string | null;
  dose?: string | null;
  dispense?: string | null;
  frequency?: string | null;
  route?: string | null;
  duration?: string | null;
  notes?: string | null;
  /** `dd/MM/yyyy` on the wire; Flutter prints the pair under DURATION at 11px (EPP:459-467). */
  startDate?: string | null;
  endDate?: string | null;
}

/** Allergy / diagnosis entries (Flutter `Allergy` / `Diagnosis`). */
export interface EPrescriptionCoding {
  display?: string | null;
  code?: string | null;
  /** Diagnosis only — "Primary" flags the principal Dx. */
  type?: string | null;
  /** Coding system; Flutter falls back to "ICD-10" when absent (patient_assessment_model:907-914). */
  system?: string | null;
}

/** Clinic letterhead — the right-hand block of Flutter's `ClinicDetailsWidget`. */
export interface EPrescriptionPractice {
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface EPrescriptionOrganization {
  id?: string | null;
  name?: string | null;
  logoUrl?: string | null;
}

/** Payer block. `sponsorType` drives which keys Flutter shows (EPP:673-685). */
export interface EPrescriptionSponsor {
  sponsorName?: string | null;
  sponsorType?: string | null;
  policyNo?: string | null;
  plan?: string | null;
  tpa?: string | null;
}

export interface EPrescriptionPreview {
  ePrescriptionReference?: string | null;
  ePerscription?: { ePrescriptionReference?: string | null; status?: string | null } | null;
  /** THE WIRE KEY IS `patientDetails` — see the header note; `patient` was never populated. */
  patientDetails?: EPrescriptionPreviewPatient | null;
  allergies?: EPrescriptionCoding[] | null;
  diagnoses?: EPrescriptionCoding[] | null;
  prescriptions?: EPrescriptionLine[] | null;
  practice?: EPrescriptionPractice | null;
  organization?: EPrescriptionOrganization | null;
  sponsor?: EPrescriptionSponsor | null;
}

/**
 * Flutter's `EPrescriptionPreviewModel.id` getter (eprescription_preview_model.dart:42-43):
 * the top-level reference, falling back to the nested one. Note the nested key's own
 * misspelling — `ePerscription` — which is the wire's, not a typo here.
 */
export function ePrescriptionId(p: EPrescriptionPreview | undefined | null): string | null {
  return p?.ePrescriptionReference ?? p?.ePerscription?.ePrescriptionReference ?? null;
}

const KEY = ['eprescription'] as const;

/** `hasEPrescriptionEnabled` — the org/provider actually has eRx credentials configured. */
export function useEPrescriptionEnabled(providerId?: string | null, practiceId?: string | null) {
  return useQuery({
    queryKey: [...KEY, 'enabled', providerId, practiceId],
    enabled: !!providerId && !!practiceId,
    staleTime: 10 * 60_000,
    queryFn: ({ signal }) =>
      post<boolean>(
        '/api/Doctor/CheckEPrescriptionLoginEnabled',
        { providerId, practiceId },
        { signal },
      ),
  });
}

/** The encounter's current eRx. Its `id` drives both the "eRx {ref}" label and the synced ✓. */
export function useEPrescriptionPreview(encounterId?: string | null, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'preview', encounterId],
    enabled: !!encounterId && enabled,
    queryFn: ({ signal }) =>
      get<EPrescriptionPreview>(
        `${ERX_BASE}/GetPrescriptionData?encounterId=${encodeURIComponent(encounterId!)}`,
        { signal },
      ),
  });
}

/**
 * Send (or re-send) the encounter's eRx to DHPO. Flutter posts with the encounterId as a QUERY
 * param and an empty body (`submitEPrescription`, :1085-1093).
 *
 * Re-sending CANCELS the previous eRx server-side — Flutter guards it behind a confirm dialog
 * ("e-Prescription is generated already… the old eRx will be cancelled", wrap_up_billing_screen
 * .dart:358-373). That confirmation is the CALLER's responsibility; this hook just sends.
 */
export function useSubmitEPrescription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (encounterId: string) =>
      post<unknown>(
        `${ERX_BASE}/SendEPrescriptionToDHPO?encounterId=${encodeURIComponent(encounterId)}`,
        {},
      ),
    onSuccess: (_d, encounterId) => {
      showSuccess('ePrescription sent');
      // The reference/status changes server-side — refetch the preview so the cluster's ✓ and
      // the "eRx {ref}" label reflect the new eRx rather than the superseded one.
      void qc.invalidateQueries({ queryKey: [...KEY, 'preview', encounterId] });
    },
    onError: (e) => showServerError('submit-eprescription', e),
  });
}
